import * as R from 'remeda';
import { getIDFromUrl, ID } from '@rossum/api-client';
import { SideloadOptions } from '@rossum/api-client/annotations';
import { Queue } from '@rossum/api-client/queues';
import { MetaField } from '@rossum/api-client/shared';
import { User } from '@rossum/api-client/users';
import { resolveAnnotationEdits } from '../document-list-base/helpers/resolveAnnotationEdits';
import { resolveModifierFromUrl } from '../document-list-base/helpers/resolveModifierFromUrl';
import { LevelOptions } from '../document-list-base/types';
import { DashboardAnnotationListWithSideload } from '../document-list-columns/utils';
import { resolveAnnotationDuplicates } from '../duplicates/helpers';

export type ResponseWithDicts = ReturnType<
  typeof transformSideloadedListsToDicts
>;

export const toDict = <T extends { url: string }>(
  items: T[] = [],
  // TODO: might be cool to allow only keys of T which values are strings or numbers
  keyProp: keyof T = 'url'
) => R.indexBy(items, item => String(item[keyProp]));

type ConvertKeysToDictionary<T, Keys extends keyof T> = {
  [K in keyof T]: K extends Keys
    ? NonNullable<T[K]> extends Array<infer Item>
      ? Record<string, Item>
      : T[K]
    : T[K];
};

export const transformSideloadedListsToDicts = (
  response: DashboardAnnotationListWithSideload
): ConvertKeysToDictionary<
  DashboardAnnotationListWithSideload,
  | 'modifiers'
  | 'confirmedBys'
  | 'documents'
  | 'assignees'
  | 'labels'
  | 'exportedBys'
  | 'rejectedBys'
  | 'deletedBys'
  | 'creators'
  | 'workflowRuns'
  | 'workflowSteps'
> => ({
  ...response,
  modifiers: toDict(response.modifiers),
  documents: toDict(response.documents),
  labels: toDict(response.labels),
  assignees: toDict(response.assignees),
  exportedBys: toDict(response.exportedBys),
  confirmedBys: toDict(response.confirmedBys),
  rejectedBys: toDict(response.rejectedBys),
  deletedBys: toDict(response.deletedBys),
  creators: toDict(response.creators),
  workflowRuns: toDict(response.workflowRuns, 'annotation'),
  workflowSteps: toDict(response.workflowSteps),
});

export type TransformedData = ReturnType<
  typeof transformDataResultsToRows
>['results'][number];

export const transformDataResultsToRows = (
  data: ResponseWithDicts,
  statusSet: Set<string>,
  queueSet: Set<number>
) => ({
  ...data,
  results: data.results.map(annotation => {
    const duplicatesUrls = resolveAnnotationDuplicates(
      annotation.url,
      annotation.relations,
      data.relations
    );
    const edit = resolveAnnotationEdits(annotation.url, data.relations);

    const attachmentRelationsList = R.uniqueBy(
      [...data.relations, ...data.childRelations].filter(
        relation => relation.type === 'attachment'
      ),
      rel => rel.id
    );

    const hasAttachments = attachmentRelationsList.some(
      relation =>
        relation.parent === annotation.url ||
        relation.annotations.includes(annotation.url)
    );

    const customRelations = R.uniqueBy(
      [...data.relations, ...data.childRelations].filter(
        relation => relation.type === 'custom'
      ),
      rel => rel.id
    ).filter(
      relation =>
        relation.parent === annotation.url ||
        relation.annotations.includes(annotation.url)
    );

    const getModifierData = (
      url: string | null,
      sideloadedUsers: Record<string, User> | undefined
    ) => resolveModifierFromUrl(url, sideloadedUsers ?? {});

    // The annotation links to its current workflow step only through its
    // workflow run, so we resolve the step name via the sideloaded run.
    const currentStep = data.workflowRuns?.[annotation.url]?.currentStep;
    const workflowStepName = currentStep
      ? (data.workflowSteps?.[currentStep]?.name ?? '')
      : '';

    return {
      ...annotation,
      restricted_access: annotation.restrictedAccess,
      training_enabled: annotation.trainingEnabled,
      workflow_step: workflowStepName,
      isOutdated:
        annotation.status === 'purged' ||
        (statusSet.size === 0 ? false : !statusSet.has(annotation.status)) ||
        (queueSet.size === 0
          ? false
          : !queueSet.has(getIDFromUrl(annotation.queue))),
      duplicates: duplicatesUrls.map(duplicateUrl =>
        data.relations.find(relation => relation.url === duplicateUrl)
      ),
      edit,
      hasAttachments,
      customRelations,
      hasWorkflowRun: data.workflowRuns?.[annotation.url] !== undefined,
      original_file_name: data.documents[annotation.document]?.originalFileName,
      attachmentStatus:
        data.documents[annotation.document]?.attachmentStatus ?? null,
      created_at: annotation.createdAt,
      modified_at: annotation.modifiedAt,
      modifier: getModifierData(annotation.modifier, data.modifiers),
      exported_at: annotation.exportedAt,
      export_failed_at: annotation.exportFailedAt,
      exported_by: getModifierData(annotation.exportedBy, data.exportedBys),
      confirmed_at: annotation.confirmedAt,
      confirmed_by: getModifierData(annotation.confirmedBy, data.confirmedBys),
      rejected_at: annotation.rejectedAt,
      rejected_by: getModifierData(annotation.rejectedBy, data.rejectedBys),
      deleted_at: annotation.deletedAt,
      deleted_by: getModifierData(annotation.deletedBy, data.deletedBys),
      assigned_at: annotation.assignedAt,
      assignees: annotation.assignees.map(url =>
        getModifierData(url, data.assignees)
      ),
      annotation: annotation.url,
      creator: getModifierData(annotation.creator, data.creators),
    } satisfies Record<
      | Exclude<MetaField, 'details' | 'actions'>
      | 'attachmentStatus'
      | 'duplicates'
      | 'edit'
      | 'hasAttachments'
      | 'customRelations'
      | 'isOutdated'
      | 'restricted_access'
      | 'hasWorkflowRun'
      | 'training_enabled',
      unknown
    >;
  }),
});

export const transformResponse = (
  response: DashboardAnnotationListWithSideload,
  statusSet: Set<string>,
  queueSet: Set<number>
) =>
  transformDataResultsToRows(
    transformSideloadedListsToDicts(response),
    statusSet,
    queueSet
  );

export const getActiveQueue = ({
  queues,
  queueId,
  level,
}: {
  queues: Queue[] | undefined;
  queueId: ID | undefined;
  level: LevelOptions | Omit<string, LevelOptions> | undefined;
}): Queue | null => {
  if (level !== 'queue' || !queues || !queueId) return null;

  return queues.find(q => q.id === queueId) ?? null;
};

export const getPageSizeFromQuery = <T extends { page_size?: string }>({
  query,
  fallbackValue,
  allowedPageSizes,
}: {
  query: T;
  fallbackValue: number;
  allowedPageSizes: number[];
}) =>
  allowedPageSizes.find(ps => ps.toString() === query?.page_size) ??
  fallbackValue;

export type SideloadValue = Partial<Record<SideloadOptions, true>>;

const sideloadMap: Partial<Record<MetaField, SideloadValue>> = {
  assignees: { assignees: true },
  modifier: { modifiers: true },
  exported_by: { exportedBys: true },
  confirmed_by: { confirmedBys: true },
  deleted_by: { deletedBys: true },
  rejected_by: { rejectedBys: true },
  creator: { creators: true },
};

export const resolveUserSideloadsByColumn = ({
  columnNames,
}: {
  columnNames: MetaField[];
}): SideloadValue => {
  return columnNames.reduce((acc, columnName) => {
    const sideloadValue = sideloadMap[columnName];
    if (sideloadValue) return { ...acc, ...sideloadValue };

    return acc;
  }, {});
};
